/**
 * Dialog controller extracted from the legacy filters dialog implementation.
 * Provides a thin wrapper around trigger/panel pairs with a shared overlay,
 * focus trapping, ESC/overlay close and responsive modal <-> popover switching.
 *
 * @typedef {import('../types.js').FiltersStore} FiltersStore
 */

const MODE_BREAKPOINT = 992;
const CLASS_PREFIX = 'props-dialog';
const ACTIVE_CLASS = `${CLASS_PREFIX}--active`;
const OVERLAY_CLASS = `${CLASS_PREFIX}__overlay`;

/** @type {MediaQueryList | null} */
const mediaQuery =
  typeof window !== 'undefined'
    ? window.matchMedia(`(min-width: ${MODE_BREAKPOINT}px)`)
    : null;

/** @type {DialogInstance | null} */
let currentDialog = null;

/** @type {HTMLDivElement | null} */
let overlayElement = null;

/** @type {Set<DialogInstance>} */
const allDialogs = new Set();

/**
 * @typedef {{
 *  trigger: HTMLElement,
 *  panel: HTMLElement,
 *  mode?: 'modal' | 'popover',
 *  onOpen?: (ctx: DialogLifecycleContext) => void,
 *  onClose?: (ctx: DialogLifecycleContext) => void,
 *  initialFocus?: HTMLElement | (() => HTMLElement | null)
 * }} CreateDialogOptions
 *
 * @typedef {{
 *  trigger: HTMLElement,
 *  panel: HTMLElement,
 *  mode: 'modal' | 'popover'
 * }} DialogLifecycleContext
 *
 * @typedef {{
 *  open: () => void,
 *  close: () => void,
 *  toggle: () => void,
 *  destroy: () => void,
 *  mode: 'modal' | 'popover',
 *  key?: string,
 * }} DialogInstance
 */

/**
 * Ensure the shared overlay exists.
 * @returns {HTMLDivElement}
 */
function ensureOverlay() {
  if (overlayElement) {
    return overlayElement;
  }
  const overlay = document.createElement('div');
  overlay.className = OVERLAY_CLASS;
  overlay.setAttribute('data-dialog-overlay', 'hidden');
  overlay.addEventListener('click', (event) => {
    // Only close when the backdrop itself is clicked, not when clicking
    // inside a dialog panel that might be rendered inside the overlay.
    if (event.target !== overlay) {
      return;
    }
    if (currentDialog) {
      currentDialog.close();
    }
  });

  document.body.appendChild(overlay);
  overlayElement = overlay;
  return overlay;
}

/**
 * Create a focus trap within the provided container.
 * @param {HTMLElement} container
 * @param {HTMLElement | (() => HTMLElement | null) | undefined} preferred
 * @returns {() => void}
 */
function trapFocus(container, preferred) {
  const previouslyFocused = /** @type {HTMLElement|null} */ (
    document.activeElement instanceof HTMLElement ? document.activeElement : null
  );
  let focusables = getFocusable(container);

  const preferredNode =
    typeof preferred === 'function' ? preferred(container) : preferred || null;

  if (preferredNode && typeof preferredNode.focus === 'function') {
    preferredNode.focus();
  } else if (focusables.length) {
    focusables[0].focus();
  }

  function handleKeydown(ev) {
    if (ev.key !== 'Tab') {
      return;
    }
    focusables = getFocusable(container);
    if (!focusables.length) {
      ev.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (ev.shiftKey) {
      if (active === first || !container.contains(active)) {
        ev.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  container.addEventListener('keydown', handleKeydown);

  return function cleanup() {
    container.removeEventListener('keydown', handleKeydown);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try {
        previouslyFocused.focus();
      } catch {
        /* ignore focus errors */
      }
    }
  };
}

/**
 * Get tabbable nodes inside root.
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
function getFocusable(root) {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(root.querySelectorAll(selector)).filter((node) => {
    if (!(node instanceof HTMLElement)) {
      return false;
    }
    if (node.hasAttribute('hidden')) {
      return false;
    }
    const style = window.getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

/**
 * Determine dialog mode based on provided option + responsive breakpoint.
 * @param {'modal'|'popover'|undefined} option
 */
function resolveMode(option) {
  if (option === 'modal' || option === 'popover') {
    return option;
  }
  return mediaQuery && mediaQuery.matches ? 'popover' : 'modal';
}

function addMediaQueryListener(handler) {
  if (!mediaQuery) {
    return;
  }
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handler);
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(handler);
  }
}

function removeMediaQueryListener(handler) {
  if (!mediaQuery) {
    return;
  }
  if (typeof mediaQuery.removeEventListener === 'function') {
    mediaQuery.removeEventListener('change', handler);
  } else if (typeof mediaQuery.removeListener === 'function') {
    mediaQuery.removeListener(handler);
  }
}

function lockBodyScroll() {
  document.body.classList.add(`${CLASS_PREFIX}--scroll-locked`);
}

function unlockBodyScroll() {
  document.body.classList.remove(`${CLASS_PREFIX}--scroll-locked`);
}

/**
 * Position popover panels relative to their trigger.
 * Only relevant in desktop mode.
 * @param {HTMLElement} trigger
 * @param {HTMLElement} panel
 */
function positionPopover(trigger, panel) {
  const triggerRect = trigger.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;

  const left = Math.min(
    Math.max(triggerRect.left, 16),
    Math.max(16, viewportWidth - panelRect.width - 16)
  );
  panel.style.setProperty('--popover-left', `${left}px`);
  panel.style.setProperty('--popover-top', `${triggerRect.bottom + 8}px`);
}

/**
 * Create a dialog instance bound to a trigger and panel element.
 * @param {CreateDialogOptions} options
 * @returns {DialogInstance}
 */
export function createDialog(options) {
  const { trigger, panel } = options;
  if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
    throw new Error('[filters-dialog] trigger and panel must be HTMLElements');
  }

  let mode = resolveMode(options.mode);
  let focusCleanup = /** @type {(() => void) | null} */ (null);
  let hasInitialised = false;

  // Remember the original DOM position so modal panels can be "ported"
  // into the overlay on mobile (to avoid stacking/transform issues on iOS),
  // and restored when switching back to popover/desktop mode.
  const originalParent = panel.parentElement;
  const originalNextSibling = panel.nextSibling;
  let isInOverlay = false;

  function mountInOverlay() {
    const overlay = ensureOverlay();
    if (!overlay || isInOverlay) {
      return;
    }
    overlay.appendChild(panel);
    isInOverlay = true;
  }

  function restorePanel() {
    if (!originalParent) {
      return;
    }
    if (!isInOverlay && panel.parentElement === originalParent) {
      return;
    }
    if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
      originalParent.insertBefore(panel, originalNextSibling);
    } else {
      originalParent.appendChild(panel);
    }
    isInOverlay = false;
  }

  const state = {
    isOpen: false,
  };

  panel.hidden = true;
  panel.setAttribute('data-dialog-state', 'closed');
  panel.setAttribute('data-dialog-mode', mode);
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');

  function updateMode() {
    const nextMode = resolveMode(options.mode);
    if (nextMode === mode) {
      return;
    }
    if (state.isOpen) {
      if (mode === 'modal') {
        mountInOverlay();
      } else {
        restorePanel();
        positionPopover(trigger, panel);
      }
    } else if (!state.isOpen) {
      panel.style.removeProperty('--popover-left');
      panel.style.removeProperty('--popover-top');
    }
    mode = nextMode;
    panel.setAttribute('data-dialog-mode', mode);
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      api.close();
    }
  }

  function onResize() {
    if (state.isOpen && mode === 'popover') {
      positionPopover(trigger, panel);
    }
  }

  function open() {
    if (state.isOpen) {
      return;
    }

    if (currentDialog && currentDialog !== api) {
      currentDialog.close();
    }

    state.isOpen = true;
    hasInitialised = true;
    currentDialog = api;
    const overlay = ensureOverlay();

    // For modal mode (mobile), render the panel inside the overlay so it
    // is above any transformed/stacked table containers on iOS Safari.
    if (mode === 'modal') {
      mountInOverlay();
    } else {
      restorePanel();
    }

    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    panel.classList.add(ACTIVE_CLASS);
    panel.setAttribute('data-dialog-state', 'open');

    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', onResize);
    addMediaQueryListener(updateMode);

    overlay.setAttribute('data-dialog-overlay', 'visible');
    overlay.classList.add(ACTIVE_CLASS);

    if (mode === 'modal') {
      lockBodyScroll();
    } else {
      positionPopover(trigger, panel);
    }

    focusCleanup = trapFocus(panel, options.initialFocus);

    options.onOpen?.({ trigger, panel, mode });
  }

  function close() {
    if (!state.isOpen) {
      return;
    }
    state.isOpen = false;

    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('data-dialog-state', 'closed');
    panel.classList.remove(ACTIVE_CLASS);
    panel.hidden = true;
    if (mode === 'popover') {
      panel.style.removeProperty('--popover-left');
      panel.style.removeProperty('--popover-top');
    }

    // When closing a modal dialog, restore the panel back to its original
    // DOM position under the props-odds container so future popovers work.
    if (mode === 'modal') {
      restorePanel();
    }

    focusCleanup?.();
    focusCleanup = null;

    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', onResize);
    removeMediaQueryListener(updateMode);

    const overlay = ensureOverlay();
    overlay.setAttribute('data-dialog-overlay', 'hidden');
    overlay.classList.remove(ACTIVE_CLASS);

    unlockBodyScroll();

    if (currentDialog === api) {
      currentDialog = null;
    }

    options.onClose?.({ trigger, panel, mode });
  }

  function toggle() {
    if (state.isOpen) {
      close();
    } else {
      open();
    }
  }

  function handleTriggerClick(event) {
    event.preventDefault();
    toggle();
  }

  trigger.addEventListener('click', handleTriggerClick);

  const api = {
    open,
    close,
    toggle,
    get mode() {
      return mode;
    },
    destroy() {
      trigger.removeEventListener('click', handleTriggerClick);
      close();
      allDialogs.delete(api);
    },
  };

  allDialogs.add(api);
  return api;
}

/**
 * Hook the dialog controller into the store for visibility telemetry.
 * @param {FiltersStore} store
 */
export function bootstrapDialogs(store) {
  if (!store || typeof store.subscribe !== 'function') {
    return;
  }
  store.subscribe((state) => {
    if (!state.dialogOpenMap || !currentDialog || !currentDialog.key) {
      return;
    }
    const shouldBeOpen = Boolean(state.dialogOpenMap[currentDialog.key]);
    if (!shouldBeOpen) {
      currentDialog.close();
    }
  });
}

/**
 * Close all dialogs (useful for tests).
 */
export function resetDialogs() {
  allDialogs.forEach((dialog) => dialog.close());
}
